import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Duo fixe : chaque réponse du quiz mène à un produit "hero" et à son partenaire
 * complémentaire (proposition client n°2 / TODO-cro-2026-08-02.md tâche 24).
 * Volontairement une table statique, pas un scoring — il n'y a que 2 duos possibles.
 *
 * @type {Record<string, {hero: string, partner: string}>}
 */
const RESULTS = {
  soir_nuit: { hero: 'nuit-calme', partner: 'digestion' },
  soir_digestion: { hero: 'digestion', partner: 'nuit-calme' },
  jour_energie: { hero: 'energie', partner: 'silhouette' },
  jour_pause: { hero: 'silhouette', partner: 'energie' },
};

/**
 * @typedef {object} QuizTisaneRefs
 * @property {HTMLElement} panelIntro
 * @property {HTMLElement} panelQ1
 * @property {HTMLElement} panelQ2Soir
 * @property {HTMLElement} panelQ2Jour
 * @property {HTMLElement} panelResult
 * @property {HTMLElement} resultHero
 * @property {HTMLElement} resultPartner
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLInputElement} [guideTagsInput]
 * @property {HTMLElement} [guideDownload]
 * @property {HTMLElement} [postedMarker]
 * @extends {Component<QuizTisaneRefs>}
 */
export class QuizTisaneComponent extends Component {
  requiredRefs = [
    'panelIntro',
    'panelQ1',
    'panelQ2Soir',
    'panelQ2Jour',
    'panelResult',
    'resultHero',
    'resultPartner',
    'addToCartButton',
  ];

  /** @type {string[]} */
  #variantIds = [];

  /** @type {string | undefined} */
  #currentResultKey;

  connectedCallback() {
    super.connectedCallback();

    // Après soumission du formulaire "guide" (rechargement de page natif Liquid),
    // on retrouve le duo choisi via le paramètre ?quiz=... plutôt que de revenir à l'intro.
    const resultKeyFromUrl = new URLSearchParams(window.location.search).get('quiz');
    const postedSuccessfully = this.refs.postedMarker?.dataset.posted === 'true';

    if (resultKeyFromUrl && RESULTS[resultKeyFromUrl]) {
      this.#applyResult(resultKeyFromUrl);
      if (this.refs.guideDownload) this.refs.guideDownload.hidden = !postedSuccessfully;
    } else {
      this.#showPanel(this.refs.panelIntro);
    }
  }

  start() {
    this.#showPanel(this.refs.panelQ1);
  }

  /** @param {MouseEvent & {target: HTMLElement}} event */
  chooseMoment(event) {
    const branch = /** @type {HTMLElement | null} */ (event.target.closest('[data-branch]'))?.dataset.branch;
    if (!branch) return;

    this.#showPanel(branch === 'soir' ? this.refs.panelQ2Soir : this.refs.panelQ2Jour);
  }

  /** @param {MouseEvent & {target: HTMLElement}} event */
  chooseResult(event) {
    const key = /** @type {HTMLElement | null} */ (event.target.closest('[data-result]'))?.dataset.result;
    if (!key || !RESULTS[key]) return;

    this.#applyResult(key);
  }

  restart() {
    this.#variantIds = [];
    this.#currentResultKey = undefined;
    history.replaceState(null, '', window.location.pathname);
    this.#showPanel(this.refs.panelIntro);
  }

  /**
   * Renseigne le tag du duo choisi dans le formulaire "guide" juste avant l'envoi natif
   * du form Liquid (pas de fetch : c'est un vrai POST + redirection, cf. contact[tags]
   * documenté par Shopify sur le form 'customer').
   */
  prepareGuideForm() {
    if (!this.#currentResultKey) return;

    const result = RESULTS[this.#currentResultKey];
    if (!result) return;

    if (this.refs.guideTagsInput) this.refs.guideTagsInput.value = `quiz-tisane, quiz-${result.hero}`;
  }

  /** @param {string} key */
  #applyResult(key) {
    const result = RESULTS[key];
    if (!result) return;

    const heroCard = this.querySelector(`[data-product="${result.hero}"]`);
    const partnerCard = this.querySelector(`[data-product="${result.partner}"]`);
    if (!heroCard || !partnerCard) {
      const missing = [!heroCard && result.hero, !partnerCard && result.partner].filter(Boolean);
      console.warn(
        `[quiz-tisane] Produit(s) manquant(s) dans les réglages de la section : ${missing.join(', ')}. ` +
          'Renseigne les 4 sélecteurs produit (section "Produits (duo fixe)") dans l\'éditeur de thème.'
      );
      return;
    }

    this.refs.resultHero.replaceChildren(heroCard.cloneNode(true));
    this.refs.resultPartner.replaceChildren(partnerCard.cloneNode(true));

    this.#variantIds = [
      /** @type {HTMLElement} */ (heroCard).dataset.variantId ?? '',
      /** @type {HTMLElement} */ (partnerCard).dataset.variantId ?? '',
    ].filter(Boolean);

    this.#currentResultKey = key;

    const url = new URL(window.location.href);
    url.searchParams.set('quiz', key);
    history.replaceState(null, '', url);

    this.#showPanel(this.refs.panelResult);
  }

  async addDuoToCart() {
    if (this.#variantIds.length !== 2) return;

    this.refs.addToCartButton.disabled = true;

    const items = this.#variantIds.map((id) => ({ id: Number(id), quantity: 1 }));

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const data = await response.json();

      if (data.status) throw new Error(data.description || data.message || 'Erreur ajout panier');

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'quiz-tisane', {
          source: 'quiz-tisane',
          itemCount: items.length,
        })
      );
    } catch (error) {
      console.error('[quiz-tisane] Ajout du duo au panier impossible', error);
    } finally {
      this.refs.addToCartButton.disabled = false;
    }
  }

  /** @param {HTMLElement} panelToShow */
  #showPanel(panelToShow) {
    for (const panel of [
      this.refs.panelIntro,
      this.refs.panelQ1,
      this.refs.panelQ2Soir,
      this.refs.panelQ2Jour,
      this.refs.panelResult,
    ]) {
      panel.hidden = panel !== panelToShow;
    }
  }
}

if (!customElements.get('quiz-tisane')) {
  customElements.define('quiz-tisane', QuizTisaneComponent);
}
